// React-based Plugin Manager for Multi-Tenant Architecture
import type { GrapesJSEditor } from '../types/grapesjs';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:4000';

import { registerCommands } from './commands'
import { registerBlocks, registerMapboxComponents } from './blocks'
import { registerCanvasFeatures } from './canvasFeatures'
import { registerSuperTooltip } from './superTooltip'
import { registerMapboxRenderer } from './mapboxRenderer'
// import { templateLoaderPlugin } from './templateLoader' // Disabled — templates handled by React TemplateLibrary
import placeholder2 from './placeholder2'
import layoutEnhancer from './layoutEnhancer'
import grapesjsGptPlugin from './grapesjsGptPlugin'
import googleTranslatePlugin from './googleTranslate'
import aiContextEnhancer from './aiContextEnhancer'
import aiAssistantPanel from './aiAssistantPanel'
import { registerContextEnhancer, registerAssistantPanel, registerGptPanel } from './aiState'

import { registerGlobalNavbar } from './globalNavbar'
import grapesjsTouch from 'grapesjs-touch'
import grapesjsTuiImageEditor from 'grapesjs-tui-image-editor'

// Initialize React-based plugins
export function initializeReactPlugins(editor: GrapesJSEditor): void {
  console.log('🎨 Initializing React-based plugins...')
  
  try {
    // Register custom commands
    registerCommands(editor)
    console.log('✅ Commands registered successfully')
    
    // Register custom blocks
    registerBlocks(editor)
    console.log('✅ Blocks registered successfully')
    
    // Register Mapbox components
    registerMapboxComponents(editor)
    console.log('✅ Mapbox components registered successfully')
    
    // Register Mapbox React renderer
    registerMapboxRenderer(editor)
    console.log('✅ Mapbox React renderer registered successfully')
    
    // Register canvas features (grid and guidelines)
    registerCanvasFeatures(editor)
    console.log('✅ Canvas features registered successfully')
    
    // Register Super Tooltip plugin
    registerSuperTooltip(editor)
    console.log('✅ Super Tooltip plugin registered successfully')
    
    // Configure device manager
    configureDeviceManager(editor)
    console.log('✅ Device manager configured')
    
    // Template management handled by React TemplateLibrary component in Topbar

    placeholder2(editor, {
      animation: 'animation1',
      dragClasses: ['animation'],
    })
    console.log('✅ Placeholder 2.0 plugin registered successfully')
    
    layoutEnhancer(editor, {
      componentFilter: ['*'],
      showInPanel: true,
      panelTitle: 'Layout Enhancer',
      // Presets use Tailwind classes so they don't fight Tailwind utility classes on the canvas
      spacingPresets: {
        'Tight':       { classes: ['m-1', 'p-2'] },
        'Comfortable': { classes: ['m-2', 'p-4'] },
        'Spacious':    { classes: ['m-4', 'p-6'] },
        'Loose':       { classes: ['m-6', 'p-8'] },
      }
    })
    console.log('✅ Layout Enhancer plugin registered successfully')
    
    try {
      const aiEnhancer = aiContextEnhancer(editor, {
        enableComponentAwareness: true,
        enableBlockAwareness: true,
        enableToolAwareness: true,
        enableStyleAwareness: true,
        enableRealEstateContext: true,
        enableCustomPrompts: true,
        maxContextLength: 4000
      });
      registerContextEnhancer(aiEnhancer);
      console.log('✅ AI Context Enhancer registered');
    } catch (error) {
      console.error('❌ Error initializing AI Context Enhancer:', error);
    }

    try {
      const aiPanel = aiAssistantPanel(editor, {
        enableComponentSuggestions: true,
        enableLayoutSuggestions: true,
        enableContentSuggestions: true,
        enableRealEstateSuggestions: true
      });
      registerAssistantPanel(aiPanel);
      console.log('✅ AI Assistant Panel registered');
    } catch (error) {
      console.error('❌ Error initializing AI Assistant Panel:', error);
    }
    
    // GPT plugin sets window.showGptPanel / window.closeGptPanel internally.
    // We initialise it on the editor:load event so the Modal and Pages APIs are guaranteed ready.
    const gptPluginOptions = {
      model: 'gpt-4',
      maxTokens: 1000,
      temperature: 0.7,
      magicIcon: '✨',
      buttonPosition: 'top-right',
      buttonSize: 50,
      zIndex: 99999,
      disableFloatingButton: true,
      contentTypes: [
        'headlines', 'paragraphs', 'descriptions', 'call-to-actions',
        'meta-descriptions', 'social-media', 'email-subject-lines',
        'product-descriptions', 'real-estate-listings', 'agent-bios'
      ],
      defaultPrompts: {
        headlines: 'Generate a compelling headline for a {topic}',
        paragraphs: 'Write a professional paragraph about {topic}',
        descriptions: 'Create a detailed description for {topic}',
        'call-to-actions': 'Generate a persuasive call-to-action for {topic}',
        'meta-descriptions': 'Write an SEO-optimized meta description for {topic}',
        'social-media': 'Create engaging social media content for {topic}',
        'email-subject-lines': 'Generate email subject lines for {topic}',
        'product-descriptions': 'Write a product description for {topic}',
        'real-estate-listings': 'Write a compelling real estate listing description for {topic}',
        'agent-bios': 'Write a professional real estate agent bio for {topic}'
      }
    };

    editor.on('load', () => {
      try {
        grapesjsGptPlugin(editor, gptPluginOptions);
        // Register panel controls in aiState so other modules get typed access
        if ((window as any).showGptPanel && (window as any).closeGptPanel) {
          registerGptPanel({ show: (window as any).showGptPanel, close: (window as any).closeGptPanel });
        }
        console.log('✅ GrapesJS GPT Plugin registered');
      } catch (error) {
        console.error('❌ Error initializing GPT Plugin:', error);
      }
    });
    
    googleTranslatePlugin(editor, {
      includedLanguages: ['en', 'es', 'fr', 'de', 'it', 'pt', 'zh', 'ja', 'ko', 'ar', 'ru', 'hi'],
      layout: 'SIMPLE',
      pageLanguage: 'auto',
      autoDisplay: false
    })
    console.log('✅ Google Translate component type registered')

    grapesjsTouch(editor)
    console.log('✅ Touch plugin registered')

    grapesjsTuiImageEditor(editor, {
      script: [
        'https://uicdn.toast.com/tui.code-snippet/v1.5.2/tui-code-snippet.min.js',
        'https://uicdn.toast.com/tui-color-picker/v2.2.7/tui-color-picker.min.js',
        'https://uicdn.toast.com/tui-image-editor/v3.15.2/tui-image-editor.min.js',
      ],
      style: [
        'https://uicdn.toast.com/tui-color-picker/v2.2.7/tui-color-picker.min.css',
        'https://uicdn.toast.com/tui-image-editor/v3.15.2/tui-image-editor.min.css',
      ],
    })
    console.log('✅ TUI Image Editor plugin registered')
    
    // Register Global Navbar (auto-injects nav on every page)
    registerGlobalNavbar(editor);
    console.log('✅ Global Navbar registered successfully');

  } catch (error) {
    console.error('❌ Failed to initialize React plugins:', error)
  }
}

// Configure device management for React
function configureDeviceManager(editor) {
  // Hide default devices since we handle this in Topbar
  editor.getConfig().showDevices = 0
  
  // Add custom device commands
  editor.Commands.add('set-device-desktop', {
    run: (editor) => editor.setDevice('Desktop')
  })
  
  editor.Commands.add('set-device-tablet', {
    run: (editor) => editor.setDevice('Tablet')
  })
  
  editor.Commands.add('set-device-mobile', {
    run: (editor) => editor.setDevice('Mobile portrait')
  })
}

// Configure template management for React - DISABLED (handled by React TemplateLibrary component)
/* function configureTemplateManager(editor) {
  // Add custom template commands
  editor.Commands.add('save-template', {
    run: (editor) => {
      const html = editor.getHtml()
      const css = editor.getCss()
      const data = editor.getProjectData()
      
      // Store in localStorage for now (can be replaced with API call)
      const templates = JSON.parse(localStorage.getItem('gjs-templates') || '[]')
      const newTemplate = {
        id: Date.now(),
        name: `Template ${templates.length + 1}`,
        html,
        css,
        data,
        timestamp: new Date().toISOString()
      }
      
      templates.push(newTemplate)
      localStorage.setItem('gjs-templates', JSON.stringify(templates))
      
      // Show success message (React-based)
      showNotification('Template saved successfully!', 'success')
    }
  })
  
  editor.Commands.add('load-template', {
    run: (editor, sender, options) => {
      if (options.template) {
        editor.setComponents(options.template.html)
        editor.setStyle(options.template.css)
        if (options.template.data) {
          editor.loadProjectData(options.template.data)
        }
        showNotification('Template loaded successfully!', 'success')
      }
    }
  })
} */

// React-based notification system
function showNotification(message, type = 'info') {
  // Create a simple notification element
  const notification = document.createElement('div')
  notification.style.cssText = `
    position: fixed;
    top: 20px;
    right: 20px;
    padding: 12px 20px;
    border-radius: 4px;
    color: white;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    font-size: 14px;
    z-index: 10000;
    background-color: ${type === 'success' ? '#4caf50' : type === 'error' ? '#f44336' : '#2196f3'};
    box-shadow: 0 2px 8px rgba(0,0,0,0.2);
    transform: translateX(100%);
    transition: transform 0.3s ease;
  `
  notification.textContent = message
  
  document.body.appendChild(notification)
  
  // Animate in
  setTimeout(() => {
    notification.style.transform = 'translateX(0)'
  }, 100)
  
  // Remove after 3 seconds
  setTimeout(() => {
    notification.style.transform = 'translateX(100%)'
    setTimeout(() => {
      document.body.removeChild(notification)
    }, 300)
  }, 3000)
}

// Get all plugins for GrapesJS React component
// gjs-blocks-basic removed — all primitives (image, video, text, link, columns)
// are already registered by src/plugins/blocks.ts, so loading this caused duplicates
export function getAllPlugins() {
  return []
}

// Plugin status and health check
export function getPluginStatus() {
  return {
    status: 'ready',
    architecture: 'react-based'
  }
} 